import { Spinner, TableCell, TableHead, TableRow } from '@/components/ui'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { DriverActivityType } from '@/modules/activities/consts'
import { useActivityColor } from '@/utils'
import { useMemo } from 'react'
import { InfoLabel } from '../driver-grade/grade-parts'
import { formatHours, perWeek } from './hours'
import { useRowSort } from './row-sort'
import { ScrollTable } from './scroll-table'
import type { DRIVER_WORK_YEAR_QUERY } from './queries'

type YearRow = ResultOf<typeof DRIVER_WORK_YEAR_QUERY>['driverWorkYear'][number]

const COLUMNS = 9

interface YearTableRow {
  id: string
  name: string
  totalHours: number
  driveHours: number
  workHours: number
  availableHours: number
  days: number
  weeks: number
  perWeek: number
  months: number[]
}

const MonthSparkline: React.FC<{ months: number[] }> = ({ months }) => {
  const getActivityColor = useActivityColor()
  const color = getActivityColor({ activityType: DriverActivityType.DRIVE })
  const top = Math.max(1, ...months)
  return (
    <div className="flex h-6 w-28 items-end gap-[2px]">
      {months.map((hours, index) => (
        <div
          key={index}
          className="flex-1 rounded-t-[1px]"
          style={{
            backgroundColor: color,
            height: `${Math.max(hours ? 6 : 2, (hours / top) * 100)}%`,
          }}
          title={formatHours(hours)}
        />
      ))}
    </div>
  )
}

export const DriverWorkYearTable: React.FC<{
  rows: YearRow[]
  loading: boolean
  onSelectDriver(userId: string): void
}> = ({ rows, loading, onSelectDriver }) => {
  const translations = useTranslate({
    driver: 'common.driver',
    total: 'driver-work.total',
    totalInfo: 'driver-work.total-info',
    driving: 'common.driving',
    otherWork: 'common.other-work',
    availability: 'common.availability',
    days: 'driver-work.days-worked',
    weeks: 'driver-work.weeks-worked',
    perWeek: 'driver-work.per-week',
    months: 'driver-work.month-by-month',
    noData: 'driver-work.no-data',
  })

  const data = useMemo<YearTableRow[]>(
    () =>
      rows.map(row => {
        const months = Array<number>(12).fill(0)
        for (const month of row.months)
          months[month.month - 1] = month.totalHours
        return {
          id: row.user.id,
          name: row.user.fullName,
          totalHours: row.totalHours,
          driveHours: row.driveHours,
          workHours: row.workHours,
          availableHours: row.availableHours,
          days: row.days,
          weeks: row.weeks,
          perWeek: perWeek(row.totalHours, row.weeks),
          months,
        }
      }),
    [rows]
  )
  const { sorted, headProps } = useRowSort(data, {
    name: row => row.name,
    total: row => row.totalHours,
    drive: row => row.driveHours,
    work: row => row.workHours,
    available: row => row.availableHours,
    days: row => row.days,
    weeks: row => row.weeks,
    perWeek: row => row.perWeek,
  })

  const hours = (value: number) => (
    <TableCell end className="tabular-nums">
      {formatHours(value)}
    </TableCell>
  )

  return (
    <ScrollTable
      header={
        <TableRow>
          <TableHead {...headProps('name')}>{translations.driver}</TableHead>
          <TableHead end {...headProps('total')}>
            <InfoLabel
              plain
              label={translations.total}
              info={translations.totalInfo}
            />
          </TableHead>
          <TableHead end {...headProps('drive')}>
            {translations.driving}
          </TableHead>
          <TableHead end {...headProps('work')}>
            {translations.otherWork}
          </TableHead>
          <TableHead end {...headProps('available')}>
            {translations.availability}
          </TableHead>
          <TableHead end {...headProps('days')}>
            {translations.days}
          </TableHead>
          <TableHead end {...headProps('weeks')}>
            {translations.weeks}
          </TableHead>
          <TableHead end {...headProps('perWeek')}>
            {translations.perWeek}
          </TableHead>
          <TableHead>{translations.months}</TableHead>
        </TableRow>
      }
    >
      {loading && (
        <TableRow>
          <TableCell colSpan={COLUMNS}>
            <Spinner />
          </TableCell>
        </TableRow>
      )}
      {!loading && !data.length && (
        <TableRow>
          <TableCell colSpan={COLUMNS} className="text-center text-stone-500">
            {translations.noData}
          </TableCell>
        </TableRow>
      )}
      {sorted.map(row => (
        <TableRow key={row.id}>
          <TableCell>
            <button
              type="button"
              className="text-left font-medium text-stone-900 underline-offset-2 hover:underline"
              onClick={() => onSelectDriver(row.id)}
            >
              {row.name}
            </button>
          </TableCell>
          <TableCell end className="font-semibold tabular-nums">
            {formatHours(row.totalHours)}
          </TableCell>
          {hours(row.driveHours)}
          {hours(row.workHours)}
          {hours(row.availableHours)}
          <TableCell end className="tabular-nums">
            {row.days}
          </TableCell>
          <TableCell end className="tabular-nums">
            {row.weeks}
          </TableCell>
          {hours(row.perWeek)}
          <TableCell>
            <MonthSparkline months={row.months} />
          </TableCell>
        </TableRow>
      ))}
    </ScrollTable>
  )
}
